"use client";
import {
  Box,
  Button,
  Dialog,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  InputAdornment,
} from "@mui/material";
import type React from "react";
import { useEffect, useState } from "react";
import DynamicTable from "../../components/lab-dynamic-table";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import DeleteConfirmationModal from "../../components/Modals/delete-modal";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PrintIcon from "@mui/icons-material/Print";
import View from "./View";
import { printDonorWithConsent } from "./print-donor-consent";
import SearchIcon from "@mui/icons-material/Search";
import debounce from "lodash.debounce";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import { useBloodBankDateQuery } from "./blood-bank-date";
import DateRangeSelector from "../../components/DateRangeSelector";

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const DonorHistory = () => {
  const [data, setData] = useState<any[]>([]);
  const [donordata, setdonordata] = useState<any[]>([]);
  const [searchType, setSearchType] = useState("donorId");
  const [searchValue, setSearchValue] = useState("");
  const [bloodGroup, setBloodGroup] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [editData, setEditData] = useState(null);
  const [open, setOpen] = useState(false);
  const [viewData, setViewData] = useState<any>();
  const [page, setPage] = useState(0);
  const {
    dateRange,
    setDateRange,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
  } = useBloodBankDateQuery(setPage);
  const [total, setTotal] = useState<any>("");
  console.log("total", total);

  const [rowsPerPage, setRowsPerPage] = useState(100);
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  const buildQueryParams = () => {
    const params: any = {};
    if (searchType && searchValue) {
      if (searchType === "name") {
        params.search = searchValue;
      } else if (searchType === "donorId") {
        params.search = searchValue;
      } else if (searchType === "cnic") {
        params.search = searchValue;
      } else if (searchType === "cellNo") {
        params.search = searchValue;
      }
    }
    if (searchType) {
      params.searchBy = searchType;
    }
    if (fromDate) {
      params.fromDate = fromDate;
    }
    if (toDate) {
      params.toDate = toDate;
    }
    if (bloodGroup) {
      params.bloodGroup = bloodGroup;
    }
    return params;
  };

  const donorData = async () => {
    try {
      const queryParams = buildQueryParams();
      const response = await axios.get(
        `${process.env.REACT_APP_BLOOD_BANK}/donor/getAllDonorsByFilter`,

        {
          params: {
            ...queryParams,
            pageNo: page + 1,
            count: rowsPerPage,
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setData(response?.data?.data);
      setTotal(response?.data?.totalCount);
    } catch (error) {
      console.error(error);
    }
  };

  const donorHistory = async () => {
    try {
      const queryParams = buildQueryParams();
      const response = await axios.get(
        `${process.env.REACT_APP_BLOOD_BANK}/donor/getAllDonorsForDashboard`,
        {
          params: {
            ...queryParams,
            pageNo: page + 1,
            count: rowsPerPage,
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setdonordata(response?.data?.data);
      setTotal(response?.data?.totalCount);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (path == "/donors") {
      donorData();
    } else {
      donorHistory();
    }
  }, [page, rowsPerPage, searchType, searchValue, fromDate, toDate, bloodGroup]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const debouncedSearch = debounce(donorData, 500);

  useEffect(() => {
    debouncedSearch();
  }, [searchType, searchValue, fromDate, toDate, bloodGroup, page, rowsPerPage]);

  const handlePrintClick = async (donor: any) => {
    if (!donor) {
      toast.error("Donor details are not available");
      return;
    }
    let header: any = {};
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BLOOD_BANK}/page-header/getPageHeader`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response?.data?.isSuccess) {
        header = response.data.data || {};
      }
    } catch (error) {
      console.error("Page header fetch failed:", error);
    }
    try {
      printDonorWithConsent(donor, header);
    } catch (error) {
      console.error("Donor print failed:", error);
      toast.error("Unable to print the donor");
    }
  };

  const handleEditClick = (id: any, data: any) => {
    setEditData(id);
    navigate("/add-donor", { state: { idd: id, data: data } });
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteClick = (id: any) => {
    setIsDeleteModalOpen(true);
    setSelectedItemId(id);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("Authorization token is missing");
        return;
      }
      const response = await axios.delete(
        `${process.env.REACT_APP_BLOOD_BANK}/donor/deleteDonorById/${selectedItemId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (path === "/donors") {
        donorData();
      } else {
        donorHistory();
      }
      toast.success(response?.data?.message);
    } catch (error: any) {
      toast.error(error.message);
    }
    setIsDeleteModalOpen(false);
  };

  const handleViewClick = (data: any) => {
    setOpen(true);
    setViewData(data);
  };

  const handleSearch = () => {
    donorData();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (searchType === "donorId") {
      if (!/^\d*$/.test(value)) {
        return;
      }
    }
    setSearchValue(value);
    setPage(0);
  };

  return (
    <Box>
      <Box sx={{ background: "#fff", p: 1, borderRadius: "8px" }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          mb={1}
        >
          <Typography sx={{ fontSize: "16px", fontWeight: "bold" }}>
            Donors History
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={() => navigate("/add-donor")}
            sx={{ fontSize: { md: "14px", sm: "12px", xs: "10px" } }}
          >
            Add Donor
          </Button>
        </Box>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Box
              display="flex"
              alignItems="flex-end"
              flexWrap="wrap"
              gap={1.5}
            >
              <FormControl size="small">
                <InputLabel>Search</InputLabel>
                <Select
                  value={searchType}
                  onChange={e => {
                    setSearchType(e.target.value);
                    setPage(0);
                  }}
                  label="Search"
                  size="small"
                  className="custom-input"
                  // sx={{
                  //   borderRadius: "12px",
                  //   border: "0px solid #D8D8D8",
                  //   background: "#F5F5FB",
                  //   fontSize: "12px",
                  //   height: "38px",
                  //   "& fieldset": {
                  //     border: "none",
                  //   },
                  //   width: "100%",
                  // }}
                  displayEmpty
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="donorId">Donor ID</MenuItem>
                  <MenuItem value="cnic">CNIC</MenuItem>
                  <MenuItem value="cellNo">Cell No</MenuItem>
                </Select>
              </FormControl>

              <TextField
                className="custom-input"
                label={`Search by ${searchType}`}
                value={searchValue}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                size="small"
                // className="search-field"
                fullWidth
                sx={{ width: { md: "180px", sm: "160px", xs: "140px" } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon
                        sx={{ cursor: "pointer" }}
                        onClick={handleSearch}
                      />
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  style: { fontSize: "12px" },
                }}
              />
              <TextField
                className="custom-input"
                select
                size="small"
                label="Blood Group"
                value={bloodGroup}
                onChange={e => {
                  setBloodGroup(e.target.value);
                  setPage(0);
                }}
                sx={{ width: { md: "130px", sm: "120px", xs: "120px" } }}
              >
                <MenuItem value="">All</MenuItem>
                {BLOOD_GROUPS.map(group => (
                  <MenuItem key={group} value={group}>
                    {group}
                  </MenuItem>
                ))}
              </TextField>
              <DateRangeSelector
                preset="blood"
                compact
                dateRange={dateRange}
                fromDate={fromDate}
                toDate={toDate}
                onDateRangeChange={value => {
                  setDateRange(value);
                  setPage(0);
                }}
                onFromDateChange={value => {
                  setFromDate(value);
                  setPage(0);
                }}
                onToDateChange={value => {
                  setToDate(value);
                  setPage(0);
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {path == "/donors" ? (
        <Box sx={{ background: "#fff", p: 0.5, mt: 1 }}>
          <DynamicTable
            headers={[
              "SN",
              "Donor ID",
              "Donor Name",
              "Father Name",
              "Blood Group",
              "Age",
              "Donation Type",
              "Pulse",
              "BP",
              "Temperature",
              "Height",
              "Weight",
              "Action",
            ]}
            data={data?.map((item: any, idx: any) => {
              return [
                page * rowsPerPage + idx + 1,
                item.donorId,
                item.name,
                item?.fatherName,
                item.bloodGroup,
                item?.age,
                item.donationType,
                `${item?.vitals?.pulse}` ? `${item?.vitals?.pulse} bpm` : "-",
                `${item?.vitals?.bloodPressure}`
                  ? `${item?.vitals?.bloodPressure} mmHg`
                  : "-",
                `${item?.vitals?.temperature}`
                  ? `${item?.vitals?.temperature} F`
                  : "-",
                `${item?.vitals?.height}`
                  ? `${item?.vitals?.height} inch`
                  : "-",
                `${item?.vitals?.weight}` ? `${item?.vitals?.weight} KG` : "-",
              ];
            })}
            loading={false}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRows={total}
            actionButtons={[
              {
                icon: (
                  <VisibilityIcon sx={{ color: "green", cursor: "pointer" }} />
                ),
                onClick: (rowIndex: any) => handleViewClick(data[rowIndex]),
              },
              {
                icon: <EditIcon sx={{ color: "#FFB800", cursor: "pointer" }} />,
                onClick: (rowIndex: any) =>
                  handleEditClick(data[rowIndex]?._id, data[rowIndex]),
              },
              {
                icon: (
                  <DeleteIcon sx={{ color: "#FF5454", cursor: "pointer" }} />
                ),
                onClick: (rowIndex: any) =>
                  handleDeleteClick(data[rowIndex]?._id),
              },
            ]}
          />
        </Box>
      ) : (
        <Box sx={{ background: "#fff", p: 0.5, mt: 1 }}>
          <DynamicTable
            headers={[
              "SN",
              "Donor ID",
              "Donor Name",
              "Father Name",
              "Blood Group",
              "Age",
              "Total Donation",
              "Last Donation Date",
              "Action",
            ]}
            data={donordata?.map((item: any, idx: any) => {
              return [
                page * rowsPerPage + idx + 1,
                item.donorId,
                item.name,
                item?.fatherName,
                item.bloodGroup,
                item?.age,
                item?.donationCount || 0,
                item?.lastDonationDate
                  ? dayjs(item?.lastDonationDate?.split("T")[0]).format("DD.MM.YYYY")
                  : "-",
              ];
            })}
            loading={false}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRows={total}
            actionButtons={[
              {
                icon: (
                  <VisibilityIcon sx={{ color: "green", cursor: "pointer" }} />
                ),
                onClick: (rowIndex: any) => handleViewClick(donordata[rowIndex]),
              },
              {
                icon: <PrintIcon sx={{ color: "#077DD3", cursor: "pointer" }} />,
                onClick: (rowIndex: any) => handlePrintClick(donordata[rowIndex]),
              },
              {
                icon: <EditIcon sx={{ color: "#FFB800", cursor: "pointer" }} />,
                onClick: (rowIndex: any) =>
                  handleEditClick(donordata[rowIndex]?._id, donordata[rowIndex]),
              },
              {
                icon: (
                  <DeleteIcon sx={{ color: "#FF5454", cursor: "pointer" }} />
                ),
                onClick: (rowIndex: any) =>
                  handleDeleteClick(donordata[rowIndex]?._id),
              },
            ]}
          />
        </Box>
      )}

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        handleClose={handleDeleteModalClose}
        handleConfirm={handleDeleteConfirm}
      />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <View data={viewData} setOpen={setOpen} />
      </Dialog>
    </Box>
  );
};

export default DonorHistory;
